import React from "react";
import LabelValuePair from "../common/LabelValuePair";
import { AuditLog, AuditLogTypeLabels } from "../../models/AuditLog";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { dialogNarrow } from "@/lib/dialogSize";

const TriggerLabels: Record<string, string> = {
  ManualUi: "Refresh Files Migration Queue",
  SqlScriptFollowup: "Execute SQL Scripts (after the script committed)",
  Api: "API call",
};

/** The server writes enum names as they are in C# ("CasesMigration"); the portal's values are camelCase. */
const migrationTypeLabel = (name: string) => {
  const value = (name.charAt(0).toLowerCase() + name.slice(1)) as MigrationType;
  return MigrationTypeLabels[value] ?? name;
};

/**
 * One request to rebuild an organization's upload order: which migration types, why, and from where.
 *
 * It records that the refresh was requested. The rebuild itself runs in the background afterwards; the
 * Migration Queue panel shows whether it is still running.
 */
export const ViewFilesPriorityRecalculationAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
}> = ({ auditLog, open, onClose }) => {
  const data = (() => {
    if (!auditLog?.additionalData) return null;
    try {
      return JSON.parse(auditLog.additionalData) as {
        Reason?: string | null;
        MigrationTypes?: string[];
        Trigger?: string;
      };
    } catch {
      return null;
    }
  })();

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`${dialogNarrow} overflow-hidden`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>{AuditLogTypeLabels[auditLog.eventType]}</DialogTitle>
            </DialogHeader>

            <Separator />

            <div className="flex flex-col gap-2 py-2">
              {data?.MigrationTypes && (
                <LabelValuePair
                  label="Migration types"
                  value={data.MigrationTypes.map(migrationTypeLabel).join(", ")}
                />
              )}
              {data?.Trigger && (
                <LabelValuePair label="Requested from" value={TriggerLabels[data.Trigger] ?? data.Trigger} />
              )}
              {data?.Reason && <LabelValuePair label="Reason" value={data.Reason} />}
              <LabelValuePair label="Performed by" value={auditLog.performerEmailAddress} />
              <LabelValuePair label="Event date" value={auditLog.eventDate} />
            </div>
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewFilesPriorityRecalculationAuditLogDetailsModal;
